import { useState } from "react";
import { Gavel } from "lucide-react";
import {
  ColoredOutlineButton,
  OUTLINE_ICON_BUTTON_ICON_CLASS,
} from "@/components/ui/colored-outline-button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTheme } from "@/context/ThemeContext";
import { useLanguage } from "@/context/LanguageContext";
import { carDetailsLabels } from "@/i18n";
import { ListingService } from "@/services/listing/listingService";

type BidRow = {
  id: string;
  amount: string;
  timestamp: string;
};

interface BidHistoryButtonProps {
  listingId: string;
}

function BidHistoryList({
  rows,
  emptyLabel,
  loading,
}: {
  rows: BidRow[];
  emptyLabel: string;
  loading: boolean;
}) {
  if (loading) {
    return (
      <p className="text-sm text-muted-foreground py-2">
        …
      </p>
    );
  }
  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-2">{emptyLabel}</p>
    );
  }
  return (
    <ul className="divide-y divide-border/50">
      {rows.map((b) => (
        <li
          key={b.id}
          className="flex items-center justify-between gap-2 py-2 text-sm"
        >
          <span className="tabular-nums font-medium">{b.amount}</span>
          <span className="text-muted-foreground tabular-nums text-xs">
            {b.timestamp}
          </span>
        </li>
      ))}
    </ul>
  );
}

const BidHistoryButton = ({ listingId }: BidHistoryButtonProps) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [rows, setRows] = useState<BidRow[]>([]);
  const [loading, setLoading] = useState(false);
  const isMobile = useIsMobile();
  const { theme } = useTheme();
  const { language } = useLanguage();
  const bi = carDetailsLabels(language).biddingInfo as Record<string, string>;
  const title = bi.bidHistory ?? "Budhistorik";
  const emptyLabel = bi.noBids ?? "Inga bud ännu";
  const isDark = theme === "dark";

  /** Always refetch on open so buyers see the latest bids. */
  const loadBids = async () => {
    setLoading(true);
    try {
      const bidsResponse = await ListingService.getListingBids(listingId);
      const bids = bidsResponse.data || [];
      const sorted = [...bids].sort((a, b) => b.amount - a.amount);
      setRows(
        sorted.map((bid) => ({
          id: bid.id,
          amount: `${bid.amount.toLocaleString("sv-SE")} :-`,
          timestamp: new Date(bid.createdAt).toLocaleString("sv-SE", {
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          }),
        })),
      );
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const openMobile = () => {
    setSheetOpen(true);
    void loadBids();
  };

  const onPopoverOpenChange = (open: boolean) => {
    setPopoverOpen(open);
    if (open) void loadBids();
  };

  const panelClass = isDark
    ? "bg-gray-900 text-white border-gray-700"
    : "bg-white text-black border-gray-200";

  return (
    <>
      {isMobile ? (
        <>
          <ColoredOutlineButton
            type="button"
            size="icon"
            variant="neutral"
            onClick={openMobile}
            aria-label={title}
            title={title}
          >
            <Gavel className={OUTLINE_ICON_BUTTON_ICON_CLASS} strokeWidth={2.35} />
          </ColoredOutlineButton>
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetContent
              side="bottom"
              nestedSheet
              overlayClassName="!z-[230]"
              className={`${
                isDark
                  ? "bg-gray-900/80 backdrop-blur-md text-white border-gray-700"
                  : "bg-white/90 backdrop-blur-md text-black border-gray-200"
              } !z-[231] rounded-t-xl`}
              onTouchStart={(e) => e.stopPropagation()}
              onTouchEnd={(e) => e.stopPropagation()}
              onTouchMove={(e) => e.stopPropagation()}
            >
              <SheetHeader>
                <SheetTitle
                  className={`${isDark ? "text-white" : "text-black"} text-center`}
                >
                  {title}
                </SheetTitle>
              </SheetHeader>
              <div
                className="py-4 overflow-y-auto overscroll-contain max-h-[min(70dvh,28rem)]"
                onTouchStart={(e) => e.stopPropagation()}
                onTouchMove={(e) => e.stopPropagation()}
              >
                <BidHistoryList
                  rows={rows}
                  emptyLabel={emptyLabel}
                  loading={loading}
                />
              </div>
            </SheetContent>
          </Sheet>
        </>
      ) : (
        <Popover open={popoverOpen} onOpenChange={onPopoverOpenChange}>
          <PopoverTrigger asChild>
            <div>
              <ColoredOutlineButton
                type="button"
                size="icon"
                variant="neutral"
                aria-label={title}
                title={title}
              >
                <Gavel
                  className={OUTLINE_ICON_BUTTON_ICON_CLASS}
                  strokeWidth={2.35}
                />
              </ColoredOutlineButton>
            </div>
          </PopoverTrigger>
          <PopoverContent
            className={`z-[220] w-72 p-4 ${panelClass} rounded-xl`}
            align="start"
          >
            <h3
              className={`font-medium mb-3 ${
                isDark ? "text-white" : "text-gray-800"
              }`}
            >
              {title}
            </h3>
            <div className="max-h-56 overflow-y-auto overscroll-contain">
              <BidHistoryList
                rows={rows}
                emptyLabel={emptyLabel}
                loading={loading}
              />
            </div>
          </PopoverContent>
        </Popover>
      )}
    </>
  );
};

export default BidHistoryButton;
